import React, { useRef } from 'react';
import { Box, IconButton, InputBase, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { THEME_COLOR } from '../../../theme/formStyles';
import { COLUMN_LABEL, GROUP_KINDS, emptyRow, inr, rowAmount } from './budget.utils';

// minWidth 0 (and size 1 on the input) lets each cell shrink to its grid column; a text input otherwise keeps
// its default ~20-character width and spills over the next column
const cellInput = { fontSize: '13px', width: '100%', minWidth: 0, '& input': { p: '6px 8px', borderRadius: '6px', minWidth: 0, width: '100%', boxSizing: 'border-box' }, '& input:focus': { boxShadow: `0 0 0 2px ${THEME_COLOR}55`, bgcolor: '#fff' } };
// Number cells: a plain input clamped to its column (width 100% of a cell that can't grow), boxed so every column
// shows even when empty, right-aligned under its heading. Text with a decimal keypad on phones, not type="number",
// whose spinner arrows shift the digits.
const numberInput = {
    display: 'block', width: '100%', minWidth: 0, boxSizing: 'border-box', m: 0, p: '6px 8px', font: 'inherit', fontSize: '13px',
    textAlign: 'right', color: '#1f2933', bgcolor: '#fff', border: '1px solid #e5e9ee', borderRadius: '6px', outline: 0,
    '&:focus': { borderColor: THEME_COLOR, boxShadow: `0 0 0 2px ${THEME_COLOR}33` },
    '&::placeholder': { color: '#b4bcc6' },
};
const cleanNumber = (v) => v.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');

// Editable lines: item name plus qty × rate, or days × persons × rate. Enter moves down a row and adds one at the end.
export default function BudgetLinesTable({ rows, kind = 'qr', total, onChange, nameLabel = 'Item' }) {
    const ref = useRef(null);
    const cols = GROUP_KINDS[kind].columns;
    const grid = `minmax(160px, 1fr) ${cols.map(() => '100px').join(' ')} 120px 34px`;

    const setCell = (i, field, value) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
    const focusCell = (row, field) => setTimeout(() => ref.current?.querySelector(`[data-cell="${row}-${field}"] input`)?.focus(), 0);
    const onKeyDown = (e, i, field) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (i === rows.length - 1) onChange([...rows, emptyRow(kind)]);
        focusCell(i + 1, field);
    };

    return (
        <Box ref={ref} sx={{ border: '1px solid #e5e9ee', borderRadius: 2, overflowX: 'auto' }}>
            <Box sx={{ minWidth: 160 + cols.length * 100 + 170 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: grid, bgcolor: '#f4f6f8', fontSize: '11px', fontWeight: 700, color: '#6b7785', textTransform: 'uppercase', '& > div': { px: 1, py: 0.75 } }}>
                    <div>{nameLabel}</div>{cols.map(c => <div key={c} style={{ textAlign: 'right', paddingRight: 14 }}>{COLUMN_LABEL[c]}</div>)}<div style={{ textAlign: 'right' }}>Amount</div><div />
                </Box>
                {rows.map((r, i) => (
                    <Box key={i} sx={{ display: 'grid', gridTemplateColumns: grid, alignItems: 'center', borderTop: '1px solid #eef1f4', '&:hover': { bgcolor: '#fafbfc' } }}>
                        <Box data-cell={`${i}-name`} sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <InputBase value={r.name} placeholder="Description" onChange={e => setCell(i, 'name', e.target.value)} onKeyDown={e => onKeyDown(e, i, 'name')} inputProps={{ size: 1 }} sx={cellInput} />
                        </Box>
                        {cols.map(c => (
                            <Box key={c} data-cell={`${i}-${c}`} sx={{ minWidth: 0, overflow: 'hidden', px: 0.5, py: 0.5 }}>
                                <Box component="input" value={r[c] ?? ''} placeholder="0" size={1} inputMode="decimal" aria-label={COLUMN_LABEL[c]}
                                    onChange={e => setCell(i, c, cleanNumber(e.target.value))} onKeyDown={e => onKeyDown(e, i, c)} sx={numberInput} />
                            </Box>
                        ))}
                        <Typography noWrap sx={{ textAlign: 'right', px: 1, fontSize: '13px', fontWeight: 500, minWidth: 0 }}>{inr(rowAmount(r, kind))}</Typography>
                        <IconButton size="small" onClick={() => onChange(rows.filter((_, idx) => idx !== i))} aria-label="Remove row"><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton>
                    </Box>
                ))}
                <Box sx={{ display: 'grid', gridTemplateColumns: grid, alignItems: 'center', borderTop: '1px solid #e5e9ee', bgcolor: '#fafbfc' }}>
                    <Box sx={{ px: 1, py: 0.5 }}>
                        <Typography component="button" type="button" onClick={() => { onChange([...rows, emptyRow(kind)]); focusCell(rows.length, 'name'); }}
                            sx={{ border: 0, bgcolor: 'transparent', color: THEME_COLOR, fontWeight: 600, fontSize: '12.5px', cursor: 'pointer', p: 0.5 }}>＋ Add row</Typography>
                    </Box>
                    {cols.map(c => <div key={c} />)}
                    <Typography sx={{ textAlign: 'right', px: 1, fontSize: '13px', fontWeight: 700 }}>{inr(total)}</Typography><div />
                </Box>
            </Box>
        </Box>
    );
}
